import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FormModal, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import SourceRequestModal from '../components/shared/SourceRequestModal';
import WorkflowListShell from '../components/workflow/WorkflowListShell';
import useEngagementListFilters from '../components/shared/useEngagementListFilters';
import { useRemovableFiles } from '../components/workflow/EditableAttachments';
import { fmtCoverageDefault, coverageExpandRow } from '../components/workflow/CoverageDisplay';
import HistoryCard from '../components/workflow/HistoryCard';
import { qcAnswerLabels, expandAnswerChanges, engagementNameMap } from './qcQuestions';
import { fetchJSON } from '../utils/api';
import {
  QC_RECORD_KIND,
  initialQcRecordForm, useQcRecordOptions, useQcRecordLocks, qcRecordFormGaps,
  saveQcRecord, applyNotice, QcTypeSection, QcCoverageSection, QcAnswersSection, QcApprovalDateSection,
  QcAttachmentsSection,
} from '../components/records/qcRecordKind';

// Toolbar filter select — the FormKit selectCls is w-full (form fields); the
// list toolbar wants intrinsic width so the controls sit on one row.
const filterSelectCls = 'px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// Timeline over qc_record_events (created/updated/deleted — never email).
// 差異列的欄位名（不印裸 key,如 `engagements`）;涵蓋/附件的串字串值由
// fmtCoverageDefault 拆成一筆一行。
const TIMELINE_LABELS = {
  entityName: 'Entity Name',
  uen: 'UEN',
  clientGroup: 'Client Group',
  yearEnd: 'Financial Year End',
  remarks: 'Remarks',
  entities: 'Companies Covered',
  engagements: 'Engagements Covered',
  files: 'Attachments',
  qcType: 'Type',
  legacyImport: 'Imported from Signed Form',
};
// Record events read like a request's history: the workflow HistoryCard, with
// the questionnaire diff unpacked one row per question (expandAnswerChanges).
export const RECORD_ACTION_LABELS = {
  created: ['Created', 'bg-green-50 text-green-700'],
  updated: ['Updated', 'bg-blue-50 text-blue-700'],
  deleted: ['Deleted', 'bg-red-50 text-red-600'],
};

// `events` given ⇒ rendered as is (the workflow record page already holds the
// record's history); otherwise fetched through the Management route.
export const RecordTimeline = ({ recordId, qcType, engagements = [], events: given = null }) => {
  const [fetched, setFetched] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (given) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`/api/qm/qc-records/${recordId}/events`);
        if (!cancelled) setFetched(data.data || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [recordId, given]);
  const events = given || fetched || [];

  const changeLabels = useMemo(() => ({ ...TIMELINE_LABELS, ...qcAnswerLabels(qcType) }), [qcType]);
  const expandChanges = useCallback(
    (c) => expandAnswerChanges(c, qcType, { engagementNames: engagementNameMap(engagements) }),
    [qcType, engagements],
  );

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  return (
    <HistoryCard
      events={events}
      actionLabels={RECORD_ACTION_LABELS}
      changeLabels={changeLabels}
      fmtChangeValue={fmtCoverageDefault}
      expandChanges={expandChanges}
    />
  );
};

// ── Edit modal ───────────────────────────────────────────────────────────────
// 欄位全部在 qcRecordKind 的共用區塊,這裡只剩 modal 外殼＋
// 存檔——New 那頁（QMQcRecordNew）用同一組區塊裝在 FormShell 裡,所以兩個入口
// 的欄位、驗證與送出內容都是同一份程式碼。形狀對齊 workflow 的 QcModifyModal。
export
const RecordFormModal = ({ record, onClose, onSaved }) => {
  const { engagements, optionsError } = useQcRecordOptions(record ? record.id : null);
  const { coveredEngagementIds, usedByOutside } = useQcRecordLocks(record ? record.id : null);

  const [form, setForm] = useState(() => initialQcRecordForm(record));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { removeIds, toggle: toggleRemove } = useRemovableFiles();

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // 空白的必填格（含問卷逐題）：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => qcRecordFormGaps(form, { record }), [form, record]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveQcRecord(form, { record, removeFileIds: removeIds });
      onSaved(saved && saved.record ? saved.record : null, applyNotice(saved && saved.applied));
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Edit ${record.qcType} Record`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
      {(error || validation.error || optionsError) && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
          <span>{error || validation.error || optionsError}</span>
        </div>
      )}
      <QcTypeSection
        value={form.qcType}
        onChange={(v) => setForm((prev) => ({ ...prev, qcType: v, answers: {} }))}
        locked={record.source === 'workflow:qcr' || usedByOutside.length > 0}
        lockReason={record.source === 'workflow:qcr'
          ? `Approved as ${record.qcType} by ${record.sourceRequestNo} — raise a QC Amendment to change it`
          : 'Engagements outside this record’s coverage reference it'}
      />
      <QcApprovalDateSection
        value={form.approvedOn}
        onChange={(v) => setField('approvedOn', v)}
        locked={record.source === 'workflow:qcr'}
      />
      <QcCoverageSection
        engagements={engagements}
        selectedIds={form.engagementIds}
        onChange={(ids) => setField('engagementIds', ids)}
        lockedIds={coveredEngagementIds}
        fallbackYearEnd={record.yearEnd}
        isEdit
      />
      <QcAnswersSection
        qcType={form.qcType}
        answers={form.answers}
        onChange={(v) => setField('answers', v)}
        engagements={form.engagementIds.map((id) => engagements.find((e) => e.id === id)).filter(Boolean)}
        legacyImport={form.legacyImport}
        signedForm={form.signedForm}
        onSignedForm={(v) => setField('signedForm', v)}
        record={record}
      />
      <QcAttachmentsSection
        files={form.files}
        onFiles={(v) => setField('files', v)}
        remarks={form.remarks}
        onRemarks={(v) => setField('remarks', v)}
        record={record}
        removeIds={removeIds}
        onToggleRemove={toggleRemove}
      />
      </FormValidationProvider>
    </FormModal>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────
// ── List page ────────────────────────────────────────────────────────────────
// Management 的記錄清單用 workflow 的樣式與元件。整列點下
// 去導向 `/qm/qc-records/:id`——記錄有自己
// 的網址,workflow 詳情頁的「Open in Management →」可以直接連到那一筆。
const QMQcRecords = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('qm:qc_records:edit');

  const [qcTypeFilter, setQcTypeFilter] = useState('');
  const [sourceFor, setSourceFor] = useState(null);
  // 篩選列與 Engagements 頁同一套；連到記錄的案有任一件符合就列出。Status
  // 晶片預設不勾——記錄大多掛在已完成的案上，照 show_by_default 會幾乎全被藏掉。
  const engFilters = useEngagementListFilters({
    optionsPath: '/api/qm/qc-records/filter-options',
    statusDefault: 'none',
  });

  // extraParams 一變 shell 就重抓並回到第一頁（它自己處理）。
  const extraParams = useMemo(() => ({
    ...engFilters.params,
    ...(qcTypeFilter ? { qcType: qcTypeFilter } : {}),
  }), [engFilters.params, qcTypeFilter]);

  const filters = (
    <>
      <select value={qcTypeFilter} onChange={(e) => setQcTypeFilter(e.target.value)} className={filterSelectCls}>
        <option value="">All types</option>
        <option value="QC6">QC6</option>
        <option value="QC7">QC7</option>
      </select>
      {engFilters.selects}
    </>
  );

  if (!engFilters.ready) return <p className="text-center text-sm text-slate-500 mt-8">Loading…</p>;

  return (
    <>
      <WorkflowListShell
        title="QC6 / QC7 Records"
        subtitle="Approved QC6 / QC7 forms on record."
        backTo="/manage"
        backLabel="Management"
        canCreate={canEdit}
        newTo="/qm/qc-records/new"
        newLabel="New Record"
        showHideCompleted={false}
        apiPath="/api/qm/qc-records"
        extraParams={extraParams}
        rowTo={(r) => `/qm/qc-records/${r.id}`}
        searchPlaceholder="Search entity / UEN / group / engagement / description / request no…"
        defaultSort={{ key: 'updatedAt', direction: 'desc' }}
        emptyText="No QC records."
        toolbarTop={engFilters.statusChips}
        filters={filters}
        columns={QC_RECORD_KIND.columns((r) => setSourceFor(r))}
        expandRow={coverageExpandRow}
      />
      {sourceFor && (
        <SourceRequestModal
          title={`Source Application · ${sourceFor.sourceRequestNo}`}
          fetchPath={`/api/qm/qc-records/${sourceFor.id}/source`}
          openHref={`/workflow/qc/${sourceFor.sourceRequestId}`}
          fields={QC_RECORD_KIND.sourceFields}
          onClose={() => setSourceFor(null)}
        />
      )}
    </>
  );
};

export default QMQcRecords;
